/**
 * Whether a name printed on a schedule is one of the people in the household.
 *
 * Used to suggest a match in the review, never to make one. The household
 * chose to be asked for a full legal name for exactly this reason: a schedule
 * prints "Daniel Tan", and the member list read "You", "Partner" and "Child".
 * Once the names are the same, being asked to pick the same name out of a list
 * is a chore with one right answer.
 *
 * NARROW ON PURPOSE, because a wrong insured person changes the totals. Cover
 * nobody has matched counts towards nothing, and cover matched to the wrong
 * person counts towards somebody who does not hold it, which is worse. So:
 *
 *  - The same words, in any order, ignoring case, accents and punctuation.
 *    Schedules print "TAN, DANIEL" as often as "Daniel Tan".
 *  - Not "one contains the other". "Daniel Tan" is a prefix of "Daniel Tan Wei
 *    Ming", who may be somebody else, and a suggestion that is sometimes a
 *    stranger teaches people to stop checking.
 *  - Exactly one member. Two people with the same name, a father and son, are
 *    the case where a suggestion would be a coin toss, so it says nothing and
 *    leaves the choice where it was.
 *
 * What a schedule prints after the name is dropped: "Daniel Tan; male;
 * non-smoker at issue" is one line and the name is the part before the first
 * semicolon.
 */

const words = (name: string): string[] =>
  (name.split(';')[0] ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter((w) => w !== '')
    .sort()

export function sameName(a: string, b: string): boolean {
  const left = words(a)
  const right = words(b)
  return left.length > 0 && left.join(' ') === right.join(' ')
}

/** The one member this name is, or null where it is nobody or could be several. */
export function suggestMember<T extends { id: string; label: string }>(
  printed: string,
  people: readonly T[],
): string | null {
  const matches = people.filter((p) => sameName(p.label, printed))
  return matches.length === 1 ? (matches[0] as T).id : null
}
